import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, Copy, Download, Eye, FileUp, Loader2, Mail, Pencil, Plus, Rocket, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { UploadButton, inputCls } from "./Fields";

const EMPTY = { name: "", description: "", image: "", url: "", tags: [], screenshots: [], coming_soon: false };

function AppForm({ initial, categories, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) => set("tags", form.tags.includes(t) ? form.tags.filter((x) => x !== t) : [...form.tags, t]);
  return (
    <div className="border border-mauve/40 bg-mauve/5 p-6 space-y-4" data-testid="app-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input data-testid="app-form-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="App name" className={inputCls} />
        <input data-testid="app-form-url" value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="App URL (https://…)" className={inputCls} />
      </div>
      <div className="flex gap-2">
        <input data-testid="app-form-image" value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="Icon / screenshot image URL" className={inputCls} />
        <UploadButton testId="app-form-image-upload" onDone={(url) => set("image", url)} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300">Screenshots — one URL per line</p>
          <UploadButton testId="app-form-screenshot-upload" onDone={(url) => set("screenshots", [...(form.screenshots || []), url])} />
        </div>
        <textarea
          data-testid="app-form-screenshots"
          value={(form.screenshots || []).join("\n")}
          onChange={(e) => set("screenshots", e.target.value.split("\n").filter((s) => s.trim() !== ""))}
          rows={3}
          className={`${inputCls} resize-y`}
        />
      </div>
      <textarea data-testid="app-form-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} placeholder="Short description" className={`${inputCls} resize-none`} />
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-2">Categories</p>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`app-form-tag-${c.toLowerCase()}`}
              onClick={() => toggleTag(c)}
              className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border transition-colors duration-200 ${
                form.tags.includes(c) ? "border-mauve bg-mauve/20 text-mauve-light" : "border-white/15 text-zinc-300 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          data-testid="app-form-coming-soon"
          checked={!!form.coming_soon}
          onChange={(e) => set("coming_soon", e.target.checked)}
          className="accent-[#9B1F6E] w-4 h-4"
        />
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-200">Coming soon (hides the Launch button)</span>
      </label>
      <div className="flex gap-2">
        <button
          data-testid="app-form-save"
          disabled={!form.name.trim() || !form.url.trim() || saving}
          onClick={() => onSave(form)}
          className="bg-mauve text-white px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-mauve-dark transition-colors duration-200 disabled:opacity-40"
        >
          Save app
        </button>
        <button data-testid="app-form-cancel" onClick={onCancel} className="border border-white/15 text-zinc-200 px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase hover:text-white transition-colors duration-200">
          Cancel
        </button>
      </div>
    </div>
  );
}

function CategoryManager({ categories }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(categories.join(", "));
  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/app-categories", { categories: value.split(",").map((s) => s.trim()).filter(Boolean) })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["app-categories"] });
      toast.success("Categories updated");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  return (
    <div className="border border-white/10 bg-surface p-6">
      <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-3">Category list (comma separated)</p>
      <div className="flex gap-2">
        <input data-testid="categories-input" value={value} onChange={(e) => setValue(e.target.value)} className={inputCls} />
        <button data-testid="categories-save" onClick={() => save.mutate()} className="bg-mauve text-white px-5 text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-mauve-dark transition-colors duration-200 shrink-0">
          Save
        </button>
      </div>
    </div>
  );
}

function PendingReviews() {
  const qc = useQueryClient();
  const { data: reviews = [] } = useQuery({ queryKey: ["admin-reviews"], queryFn: async () => (await api.get("/admin/reviews")).data });
  const moderate = useMutation({
    mutationFn: async ({ id, approved }) => (await api.patch(`/admin/reviews/${id}`, { approved })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-reviews"] });
      toast.success("Review updated");
    },
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/reviews/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-reviews"] });
      toast.success("Review deleted");
    },
  });
  const pending = reviews.filter((r) => !r.approved);
  const approved = reviews.filter((r) => r.approved);

  const Row = ({ r }) => (
    <div data-testid={`admin-review-${r.id}`} className={`border p-4 ${r.approved ? "border-white/10 bg-surface" : "border-mauve/40 bg-mauve/5"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-silver font-bold">
          {r.name} <span className="text-zinc-300 font-normal">on {r.app_name}</span>
          <span className="ml-2 inline-flex items-center gap-1 text-mauve-light text-xs"><Star size={11} className="fill-mauve text-mauve-light" /> {r.rating}/5</span>
        </p>
        <div className="flex items-center gap-2">
          {!r.approved && (
            <button data-testid={`review-approve-${r.id}`} onClick={() => moderate.mutate({ id: r.id, approved: true })} title="Approve" className="text-green-400/70 hover:text-green-400 transition-colors duration-200">
              <Check size={16} />
            </button>
          )}
          {r.approved && (
            <button data-testid={`review-unapprove-${r.id}`} onClick={() => moderate.mutate({ id: r.id, approved: false })} title="Unpublish" className="text-zinc-300 hover:text-white transition-colors duration-200">
              <X size={16} />
            </button>
          )}
          <button data-testid={`review-delete-${r.id}`} onClick={() => del.mutate(r.id)} title="Delete" className="text-zinc-300 hover:text-red-400 transition-colors duration-200">
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      <p className="mt-2 text-sm text-zinc-200">{r.comment}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-mauve-light mb-3">Awaiting approval ({pending.length})</p>
        {pending.length === 0 ? (
          <p data-testid="pending-reviews-empty" className="text-zinc-300 text-sm border border-dashed border-white/15 p-6 text-center">Nothing awaiting approval.</p>
        ) : (
          <div className="space-y-3">{pending.map((r) => <Row key={r.id} r={r} />)}</div>
        )}
      </div>
      {approved.length > 0 && (
        <div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-3">Published ({approved.length})</p>
          <div className="space-y-3">{approved.map((r) => <Row key={r.id} r={r} />)}</div>
        </div>
      )}
    </div>
  );
}

const CSV_TEMPLATE = [
  "name,url,status,owner,created date,description,category",
  'Ledger,https://ledger.klyxbs.com,Live,Kenneth L. Clarke,2026-05-01,"Personal finance tracker that works offline","Finance; Productivity"',
  'Tutor,https://tutor.klyxbs.com,Beta,Kenneth L. Clarke,2026-06-12,"Spaced-repetition study companion",Education',
].join("\n");

function downloadTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "klyx-apps-template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function CsvImport({ onImported }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/apps/import", fd);
      setSummary(res.data);
      onImported();
      toast.success(`Imported ${res.data.imported} app${res.data.imported === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <input ref={ref} data-testid="csv-import-input" type="file" accept=".csv,text/csv" className="hidden" onChange={pick} />
        <button
          data-testid="csv-import-button"
          disabled={busy}
          onClick={() => ref.current?.click()}
          className="inline-flex items-center gap-2 border border-mauve/40 text-mauve-light px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-mauve hover:text-white transition-colors duration-200 disabled:opacity-40"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />} Import CSV
        </button>
        <button
          data-testid="csv-template-button"
          onClick={downloadTemplate}
          className="inline-flex items-center gap-2 border border-white/15 text-zinc-300 px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-medium hover:border-mauve hover:text-white transition-colors duration-200"
        >
          <Download size={14} /> Download template
        </button>
        <span className="font-mono text-[10px] tracking-widest uppercase text-zinc-400">Columns: name, url, status, owner, created date (+ optional description, category)</span>
      </div>
      {summary && (
        <div data-testid="csv-import-summary" className="mt-4 border border-mauve/40 bg-mauve/5 p-5">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-bold text-silver">
              Import complete — {summary.imported} of {summary.total_rows} row{summary.total_rows === 1 ? "" : "s"} added
              {summary.skipped.length > 0 && <span className="text-zinc-200 font-normal">, {summary.skipped.length} skipped</span>}
            </p>
            <button data-testid="csv-import-dismiss" onClick={() => setSummary(null)} className="text-zinc-300 hover:text-white transition-colors duration-200">
              <X size={16} />
            </button>
          </div>
          {summary.new_categories.length > 0 && (
            <p className="mt-2 text-xs text-mauve-light">New categories added: {summary.new_categories.join(", ")}</p>
          )}
          {summary.skipped.length > 0 && (
            <ul className="mt-3 space-y-1">
              {summary.skipped.map((s) => (
                <li key={s.row} data-testid={`csv-skip-row-${s.row}`} className="text-xs text-zinc-200">
                  Row {s.row}{s.name ? ` (${s.name})` : ""}: {s.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function WaitlistSection() {
  const qc = useQueryClient();
  const { data: waitlist = [] } = useQuery({ queryKey: ["admin-waitlist"], queryFn: async () => (await api.get("/admin/waitlist")).data });
  const { data: emailStatus } = useQuery({ queryKey: ["email-status"], queryFn: async () => (await api.get("/admin/email-status")).data });
  const announce = useMutation({
    mutationFn: async (appId) => (await api.post(`/admin/apps/${appId}/announce`)).data,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin-waitlist"] });
      toast.success(`Launch announcement sent to ${res.sent} subscriber${res.sent === 1 ? "" : "s"}${res.failed ? ` (${res.failed} failed)` : ""}`);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const emailBanner = emailStatus && !emailStatus.enabled && (
    <p data-testid="email-disabled-banner" className="mb-4 border border-mauve/40 bg-mauve/5 p-4 text-xs text-zinc-300">
      <Mail size={13} className="inline mr-2 text-mauve-light" />
      Email alerts are <span className="text-mauve-light font-bold">OFF</span> — add your Gmail app password (GMAIL_APP_PASSWORD) to enable enquiry/review alerts and launch announcements.
    </p>
  );
  if (waitlist.length === 0)
    return (
      <div>
        {emailBanner}
        <p data-testid="waitlist-empty" className="text-zinc-300 text-sm border border-dashed border-white/15 p-6 text-center">No launch-notification signups yet.</p>
      </div>
    );
  const copy = async (emails) => {
    await navigator.clipboard.writeText(emails.join(", "));
    toast.success("Emails copied to clipboard");
  };
  return (
    <div data-testid="waitlist-section">
      {emailBanner}
      <div className="space-y-3">
      {waitlist.map((w) => (
        <div key={w.app_id} data-testid={`waitlist-${w.app_id}`} className="border border-white/10 bg-surface p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-heading font-bold text-silver inline-flex items-center gap-2">
              <Bell size={14} className="text-mauve-light" /> {w.app_name}
              <span className="font-mono text-[10px] tracking-widest uppercase text-mauve-light border border-mauve/40 px-2 py-0.5">{w.emails.length} waiting</span>
            </p>
            <div className="flex items-center gap-4">
              <button
                data-testid={`waitlist-announce-${w.app_id}`}
                disabled={announce.isPending}
                onClick={() => announce.mutate(w.app_id)}
                className="inline-flex items-center gap-1.5 bg-mauve text-white px-4 py-2 text-[10px] font-mono tracking-widest uppercase font-bold hover:bg-mauve-dark transition-colors duration-200 disabled:opacity-40"
              >
                <Mail size={12} /> Announce launch
              </button>
              <button
                data-testid={`waitlist-copy-${w.app_id}`}
                onClick={() => copy(w.emails)}
                className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase text-zinc-200 hover:text-white transition-colors duration-200"
              >
                <Copy size={12} /> Copy all
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs text-zinc-300 break-all">{w.emails.join(" · ")}</p>
        </div>
      ))}
      </div>
    </div>
  );
}

export function AppsTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const { data: apps = [] } = useQuery({ queryKey: ["admin-apps"], queryFn: async () => (await api.get("/admin/apps")).data });
  const { data: catData } = useQuery({ queryKey: ["app-categories"], queryFn: async () => (await api.get("/apps/categories")).data });
  const categories = catData?.categories || [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-apps"] });
    qc.invalidateQueries({ queryKey: ["apps"] });
  };
  const create = useMutation({
    mutationFn: async (form) => (await api.post("/admin/apps", form)).data,
    onSuccess: () => { setAdding(false); refresh(); toast.success("App added"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const update = useMutation({
    mutationFn: async ({ id, form }) => (await api.put(`/admin/apps/${id}`, form)).data,
    onSuccess: () => { setEditing(null); refresh(); toast.success("App updated"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/apps/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("App removed"); },
  });

  return (
    <div className="space-y-8" data-testid="apps-admin-tab">
      <CategoryManager key={categories.join("|")} categories={categories} />

      {adding ? (
        <AppForm initial={EMPTY} categories={categories} saving={create.isPending} onSave={(f) => create.mutate(f)} onCancel={() => setAdding(false)} />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="app-add-button"
              onClick={() => { setAdding(true); setEditing(null); }}
              className="inline-flex items-center gap-2 bg-mauve text-white px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-mauve-dark transition-colors duration-200"
            >
              <Plus size={14} /> Add app
            </button>
          </div>
          <CsvImport onImported={() => { refresh(); qc.invalidateQueries({ queryKey: ["app-categories"] }); }} />
        </div>
      )}

      <div className="space-y-4" data-testid="admin-apps-list">
        {apps.map((a) =>
          editing === a.id ? (
            <AppForm
              key={a.id}
              initial={{ name: a.name, description: a.description, image: a.image, url: a.url, tags: a.tags || [], screenshots: a.screenshots || [], coming_soon: !!a.coming_soon }}
              categories={categories}
              saving={update.isPending}
              onSave={(f) => update.mutate({ id: a.id, form: f })}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div key={a.id} data-testid={`admin-app-${a.id}`} className="border border-white/10 bg-surface p-5 flex flex-wrap items-center gap-5">
              {a.image ? (
                <img src={a.image} alt={a.name} className="w-20 h-14 object-cover border border-white/10 shrink-0" />
              ) : (
                <div className="w-20 h-14 border border-white/10 bg-elevated shrink-0" />
              )}
              <div className="flex-1 min-w-[200px]">
                <p className="font-heading font-bold text-silver">
                  {a.name}
                  {a.coming_soon && <span className="ml-2 font-mono text-[9px] tracking-[0.2em] uppercase text-mauve-light border border-mauve/40 px-1.5 py-0.5">Coming soon</span>}
                </p>
                <p className="text-xs text-zinc-300 mt-1">{(a.tags || []).join(" · ") || "No categories"}</p>
                {a.meta && Object.keys(a.meta).length > 0 && (
                  <p data-testid={`app-meta-${a.id}`} className="font-mono text-[10px] tracking-widest uppercase text-zinc-400 mt-1.5">
                    {[a.meta.status && `Status: ${a.meta.status}`, a.meta.owner && `Owner: ${a.meta.owner}`, a.meta.created_date && `Created: ${a.meta.created_date}`]
                      .filter(Boolean)
                      .join("  ·  ")}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-5 font-mono text-[11px] text-zinc-200">
                <span className="inline-flex items-center gap-1.5" title="Views" data-testid={`app-views-${a.id}`}><Eye size={13} className="text-zinc-300" /> {a.views}</span>
                <span className="inline-flex items-center gap-1.5" title="Launches" data-testid={`app-launches-${a.id}`}><Rocket size={13} className="text-zinc-300" /> {a.launches}</span>
                <span className="inline-flex items-center gap-1.5" title="Average rating"><Star size={13} className="text-mauve-light fill-mauve" /> {a.avg_rating} ({a.rating_count})</span>
                {a.pending_reviews > 0 && <span className="text-mauve-light">{a.pending_reviews} pending</span>}
              </div>
              <div className="flex items-center gap-3">
                <button data-testid={`app-edit-${a.id}`} onClick={() => { setEditing(a.id); setAdding(false); }} className="text-zinc-300 hover:text-white transition-colors duration-200" title="Edit">
                  <Pencil size={16} />
                </button>
                <button data-testid={`app-delete-${a.id}`} onClick={() => del.mutate(a.id)} className="text-zinc-300 hover:text-red-400 transition-colors duration-200" title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          )
        )}
      </div>

      <div>
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-mauve-light mb-4">Launch waitlist</p>
        <WaitlistSection />
      </div>

      <div>
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-mauve-light mb-4">Review moderation</p>
        <PendingReviews />
      </div>
    </div>
  );
}
